<template>
  <div class="mc-cardwrap">
    <section class="bp-card" :data-bpbrand="brand" :aria-labelledby="titleId">
      <div class="bp-hd">
        <img class="bp-av" :src="mayaImage" alt="">
        <div><h2 :id="titleId" class="bp-h">{{ t("bp_h") }}</h2><p class="bp-sub">{{ t("bp_sub") }}</p></div>
      </div>

      <fieldset class="bp-sec">
        <legend class="bp-lb">{{ t("bp_color") }}</legend>
        <div class="bp-colrow">
          <button type="button" class="bp-auto" :class="{ on: !isHex(state.theme_color) }" :aria-pressed="!isHex(state.theme_color)" @click="setColor('')">{{ t("bp_color_auto") }}</button>
          <button v-for="color in colors" :key="color.value" type="button" class="bp-cs" :class="{ on: state.theme_color.toLowerCase() === color.value.toLowerCase() }" :style="{ background: color.value }" :title="t(color.labelKey)" :aria-label="t(color.labelKey)" :aria-pressed="state.theme_color.toLowerCase() === color.value.toLowerCase()" @click="setColor(color.value)"></button>
          <label class="bp-cpick" :title="t('ws_color_custom')"><input v-model="customColor" type="color" class="bp-cinput" :aria-label="t('ws_color_custom')" @input="setColor(customColor)"><span aria-hidden="true">+</span></label>
        </div>
      </fieldset>

      <fieldset class="bp-sec">
        <legend class="bp-lb">{{ t("bp_style") }}</legend>
        <div class="bp-pills">
          <button type="button" class="bp-pill" :class="{ on: !state.family }" :aria-pressed="!state.family" @click="setFamily('')">{{ t("bp_style_auto") }}</button>
          <button v-for="family in families" :key="family.id" type="button" class="bp-pill" :class="{ on: state.family === family.id }" :aria-pressed="state.family === family.id" @click="setFamily(family.id)">{{ familyName(family) }}</button>
        </div>
      </fieldset>

      <fieldset class="bp-sec">
        <legend class="bp-lb">{{ t("bp_cta") }}</legend>
        <p class="bp-lbnote">{{ t("bp_cta_note") }}</p>
        <div class="bp-pills">
          <button v-for="cta in ctas" :key="cta.value" type="button" class="bp-pill" :class="{ on: state.cta === cta.value }" :aria-pressed="state.cta === cta.value" @click="setCta(cta.value)">{{ t(cta.key) }}</button>
        </div>
        <div v-if="ctaPreview" class="bp-ctaprev"><span class="bp-cpl">{{ t("bp_cta_prev") }}</span><span class="bp-cpb">{{ ctaPreview }}</span></div>
        <p v-if="ctaPreview" class="bp-lbnote">{{ t("bp_cta_en") }}{{ state.cta ? "" : " " + t("bp_cta_defnote", { d: ctaDefault }) }}</p>
      </fieldset>

      <fieldset class="bp-sec">
        <legend class="bp-lb">{{ t("bp_contact") }}</legend>
        <div class="bp-inrow">
          <label class="sr-only" :for="emailId">{{ t("bp_email_ph") }}</label><input :id="emailId" v-model.trim="state.email" type="email" class="bp-in" :placeholder="t('bp_email_ph')" @input="save">
          <label class="sr-only" :for="phoneId">{{ t("bp_phone_ph") }}</label><input :id="phoneId" v-model.trim="state.phone" type="tel" class="bp-in" :placeholder="t('bp_phone_ph')" @input="save">
        </div>
      </fieldset>

      <div class="bp-acts">
        <button type="button" class="bp-skip" :disabled="busy" @click="finish(null)">{{ t("bp_skip") }}</button>
        <button type="button" class="bp-go" :disabled="busy" @click="finish({ ...state })">{{ t("bp_start") }}</button>
      </div>
      <p v-if="loadError" class="bp-lbnote" role="status">{{ loadError }}</p>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";

import { api, assetUrl } from "../api/client";
import { useI18n } from "../useI18n";

const props = defineProps({ brand: { type: String, default: "" }, busy: { type: Boolean, default: false } });
const emit = defineEmits(["start"]);
const { t, lang } = useI18n();
const mayaImage = assetUrl("/assets/img/maya-256.webp");
const storageKey = computed(() => `soa_bp_${props.brand || ""}`);
const titleId = `maya-build-prep-${Math.random().toString(36).slice(2)}`;
const emailId = `${titleId}-email`, phoneId = `${titleId}-phone`;
const state = reactive({ theme_color: "", family: "", cta: "", email: "", phone: "" });
const customColor = ref("#2847c4"), families = ref([]), ctaLabels = ref(null), ctaDefault = ref(""), loadError = ref("");
const colors = [
  { value: "#2847C4", labelKey: "bp_color_indigo" }, { value: "#0E8A6D", labelKey: "bp_color_emerald" },
  { value: "#BA3B1D", labelKey: "bp_color_vermilion" }, { value: "#7A5FB0", labelKey: "bp_color_violet" },
  { value: "#0F766E", labelKey: "bp_color_teal" }, { value: "#B7791F", labelKey: "bp_color_amber" },
  { value: "#C2255C", labelKey: "bp_color_rose" }, { value: "#1F2937", labelKey: "bp_color_charcoal" },
];
const ctas = [
  { value: "", key: "bp_cta_none" }, { value: "quote", key: "bp_cta_quote" },
  { value: "book", key: "bp_cta_book" }, { value: "buy", key: "bp_cta_buy" },
  { value: "contact", key: "bp_cta_contact" },
];
const isHex = (value) => /^#[0-9a-f]{6}$/i.test(value || "");
const ctaPreview = computed(() => state.cta ? ctaLabels.value?.[state.cta] || "" : ctaDefault.value);
function familyName(family) { return family?.name_i18n?.[lang.value] || family?.name || family?.id || ""; }
function save() { try { localStorage.setItem(storageKey.value, JSON.stringify(state)); } catch { /* Storage is optional. */ } }
function setColor(value) { state.theme_color = value; if (isHex(value)) customColor.value = value; save(); }
function setFamily(value) { state.family = value; save(); }
function setCta(value) { state.cta = value; save(); }
function finish(prep) { try { localStorage.removeItem(storageKey.value); } catch { /* Storage is optional. */ } emit("start", prep); }

onMounted(async () => {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey.value) || "null");
    if (saved && typeof saved === "object") Object.assign(state, saved);
    if (isHex(state.theme_color)) customColor.value = state.theme_color;
  } catch { /* Ignore malformed or unavailable storage. */ }
  try {
    const data = await api("/api/site/themes");
    families.value = data?.families || [];
    ctaLabels.value = data?.cta_labels || null;
    ctaDefault.value = data?.cta_default || "";
  } catch {
    // The old card remained usable with automatic defaults when the manifest was unavailable.
    loadError.value = "";
  }
});
</script>

<style scoped>
.bp-sec { min-inline-size: 0; margin-inline: 0; padding-inline: 0; border: 0; }
.bp-h, .bp-sub, .bp-lbnote { margin-block: 0; }
.bp-cs:focus-visible, .bp-auto:focus-visible, .bp-pill:focus-visible, .bp-cpick:focus-within, .bp-in:focus-visible, .bp-go:focus-visible, .bp-skip:focus-visible { outline: 3px solid var(--indigo); outline-offset: 2px; }
</style>
